import { useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { AppContext } from '../../contexts/AppContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppStore } from '../../store/appStore';

/**
 * One filter system for every list screen (orders, products, vendors, categories, data lists):
 * screens describe their filters as fields; FilterSheet edits them, ListFilterBar shows
 * quick views / saved views / active chips, and useListFilters remembers the screen's last
 * filters and the user's saved views on this device (per business).
 */

export * from './listFilterValues';
import { FilterValues, QuickView, SavedView } from './listFilterValues';
import { createListFilterStore, emptyListFilterState } from './listFilterStorage';

/**
 * Filters / sort / search / custom fields of one list screen, remembered on this device,
 * plus quick views (defined by the screen) and saved views (named by the user).
 * `ready` turns true once the remembered state has been read — load the list after that.
 */
export function useListFilters(screen: string, options: { quickViews?: QuickView[]; defaultSort?: string; remember?: boolean } = {}) {
  const business: any = useAppStore((s: any) => s.currentBusiness);
  const businessId = String(business?._id || business?.id || '');
  const remember = options.remember !== false;
  const store = useMemo(() => createListFilterStore(AsyncStorage, businessId, screen, options.defaultSort, remember), [businessId, screen, options.defaultSort, remember]);
  const { state, views, ready } = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const setState = store.updateState;

  useEffect(() => store.restore(), [store]);

  // Opened from global search's "See all": show everything matching that search, not the remembered filters.
  const routeQuery = useContext(AppContext)?.routeParams?.q;
  useEffect(() => {
    if (!ready || typeof routeQuery !== 'string' || !routeQuery.trim()) return;
    setState((s) => ({ ...emptyListFilterState(options.defaultSort), sort: s.sort, search: routeQuery.trim() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, routeQuery, store]);

  const setFilters = useCallback((filters: FilterValues) => setState((s) => ({ ...s, filters, view: undefined })), [setState]);
  const setFilter = useCallback((key: string, value: any) => setState((s) => {
    const filters = { ...s.filters };
    if (value === undefined) delete filters[key]; else filters[key] = value;
    return { ...s, filters, view: undefined };
  }), [setState]);
  const setSort = useCallback((sort?: string) => setState((s) => ({ ...s, sort })), [setState]);
  const setSearch = useCallback((search: string) => setState((s) => ({ ...s, search })), [setState]);
  const setCustomFields = useCallback((customFields: Record<string, string[]>) => setState((s) => ({ ...s, customFields, view: undefined })), [setState]);
  const apply = useCallback((next: { filters: FilterValues; sort?: string; customFields?: Record<string, string[]> }) =>
    setState((s) => ({ ...s, filters: next.filters, sort: next.sort, customFields: next.customFields ?? s.customFields, view: undefined })), [setState]);
  const reset = useCallback(() => setState((s) => ({ ...emptyListFilterState(options.defaultSort), search: s.search })), [setState, options.defaultSort]);

  const applyView = useCallback((key: string) => {
    const quick = options.quickViews?.find((v) => v.key === key);
    if (quick) { setState((s) => ({ ...s, filters: { ...quick.filters }, customFields: {}, sort: quick.sort ?? s.sort, view: key })); return; }
    const saved = store.getSnapshot().views.find((v) => v.id === key);
    if (saved) setState((s) => ({ ...s, filters: { ...saved.filters }, customFields: saved.customFields || {}, sort: saved.sort ?? s.sort, search: saved.search ?? s.search, view: key }));
  }, [options.quickViews, store, setState]);

  const saveView = useCallback((name: string) => {
    const { state } = store.getSnapshot();
    const view: SavedView = { id: `v${Date.now().toString(36)}`, name: name.trim().slice(0, 40), filters: state.filters, sort: state.sort, customFields: state.customFields, ...(state.search ? { search: state.search } : {}) };
    store.updateViews((views) => [...views, view].slice(-20));
    setState((s) => ({ ...s, view: view.id }));
  }, [store, setState]);

  const deleteView = useCallback((id: string) => {
    store.updateViews((views) => views.filter((v) => v.id !== id));
    setState((s) => (s.view === id ? { ...s, view: undefined } : s));
  }, [store, setState]);

  return useMemo(() => ({
    state, ready, views, quickViews: options.quickViews || [],
    setFilters, setFilter, setSort, setSearch, setCustomFields, apply, reset, applyView, saveView, deleteView,
  }), [state, ready, views, options.quickViews, setFilters, setFilter, setSort, setSearch, setCustomFields, apply, reset, applyView, saveView, deleteView]);
}

export type ListFiltersController = ReturnType<typeof useListFilters>;
